import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { Ban, Boxes, GitBranch, Package, ShieldAlert } from 'lucide-react';
import { DataTable, type DataFetchParams } from '@/components/DataTable';
import { MetricCard } from '@/components/ui/metric-card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  AggregatedPackage,
  AggregatedRepository,
} from '@kirin/api-client/contracts/agent-command-events';
import { useAgentCommandEventsStats } from '@/hooks/api/useAgentCommandEvents';
import {
  createAggregatedPackageColumns,
  createAggregatedRepositoryColumns,
} from './columns';
import {
  aggregatedPackagesQueryKey,
  aggregatedRepositoriesQueryKey,
  fetchAggregatedPackages,
  fetchAggregatedRepositories,
} from './api';
import {
  AggregatedPackageDetailDialog,
  AggregatedRepositoryDetailDialog,
  PackageOccurrencesDialog,
  RepositoryOccurrencesDialog,
} from './AggregatedPackageDialogs';
import { WhatIsTrackedDialog } from './WhatIsTrackedDialog';
import {
  AGENT_COMMAND_PAGE_SIZE,
  AGENT_COMMAND_TABLE_IDS,
  DEFAULT_TIME_RANGE,
  TIME_RANGE_OPTIONS,
  timeRangeToFromIso,
  type TimeRangeValue,
} from './constants';

/**
 * Agents Activity → Packages & Repos (KIR-3483). Two tabs (Packages default /
 * Repositories), stat cards, a time-range filter, sortable + paginated tables,
 * a row → detail dialog, and the KNOWN-BAD red left-stripe. One component drives
 * both the org view (organizationId from the route) and the personal view (no
 * organizationId → personal-account API).
 *
 * ponytail: agent/user multi-select filters from the ticket are deferred — v1 is
 * Claude-only (one agent) and there is no user-options endpoint yet. Time-range
 * + free-text search cover the useful cases; the query plumbing already carries
 * agentNames/userIds for when those selects land.
 */
export default function AgentsActivityTab(): ReactElement {
  const { organizationId } = useParams<{ organizationId?: string }>();
  const [timeRange, setTimeRange] =
    useState<TimeRangeValue>(DEFAULT_TIME_RANGE);
  // Aggregated views (KIR-3807): each dialog tracks its selected row.
  const [detailPackage, setDetailPackage] = useState<AggregatedPackage | null>(
    null
  );
  const [occurrencesPackage, setOccurrencesPackage] =
    useState<AggregatedPackage | null>(null);
  const [detailRepo, setDetailRepo] = useState<AggregatedRepository | null>(
    null
  );
  const [occurrencesRepo, setOccurrencesRepo] =
    useState<AggregatedRepository | null>(null);

  const { data: stats } = useAgentCommandEventsStats(organizationId);

  const from = useMemo(() => timeRangeToFromIso(timeRange), [timeRange]);
  const extra = useMemo(() => ({ from }), [from]);

  const packageColumns = useMemo(
    () =>
      createAggregatedPackageColumns(organizationId, {
        onShowOccurrences: setOccurrencesPackage,
      }),
    [organizationId]
  );
  const gitColumns = useMemo(
    () =>
      createAggregatedRepositoryColumns(organizationId, {
        onShowOccurrences: setOccurrencesRepo,
      }),
    [organizationId]
  );

  const getRowClassName = useCallback(
    (row: { knownBad: boolean }) =>
      row.knownBad ? 'border-l-4 border-l-destructive' : undefined,
    []
  );

  const initialState = useMemo(
    () => ({ pagination: { pageIndex: 0, pageSize: AGENT_COMMAND_PAGE_SIZE } }),
    []
  );

  const packagesQueryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchAggregatedPackages(organizationId, extra, params, signal),
    [organizationId, extra]
  );
  const packagesQueryKeys = useCallback(
    (params: DataFetchParams) =>
      aggregatedPackagesQueryKey(organizationId, extra, params),
    [organizationId, extra]
  );

  const reposQueryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchAggregatedRepositories(organizationId, extra, params, signal),
    [organizationId, extra]
  );
  const reposQueryKeys = useCallback(
    (params: DataFetchParams) =>
      aggregatedRepositoriesQueryKey(organizationId, extra, params),
    [organizationId, extra]
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <MetricCard
          variant="info"
          label="Packages installed (24h)"
          value={stats?.packageEvents24h ?? 0}
          icon={Package}
        />
        <MetricCard
          variant="info"
          label="Repos accessed (24h)"
          value={stats?.gitEvents24h ?? 0}
          icon={GitBranch}
        />
        <MetricCard
          variant="danger"
          label="Known-bad detected (24h)"
          value={stats?.knownBadDetected24h ?? 0}
          icon={ShieldAlert}
          muteWhenZero
        />
        <MetricCard
          variant="neutral"
          label="Blocked today"
          value={stats?.blockedToday ?? 0}
          icon={Ban}
          muteWhenZero
        />
        <MetricCard
          variant="neutral"
          label="Unique sources / repos (7d)"
          value={stats?.uniqueSourcesRepos7d ?? 0}
          icon={Boxes}
        />
      </div>

      <Tabs defaultValue="packages" className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <TabsList>
              <TabsTrigger value="packages">Packages</TabsTrigger>
              <TabsTrigger value="repositories">Repositories</TabsTrigger>
            </TabsList>
            <WhatIsTrackedDialog />
          </div>
          <Select
            value={timeRange}
            onValueChange={value => setTimeRange(value as TimeRangeValue)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_RANGE_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <TabsContent value="packages">
          <DataTable<AggregatedPackage>
            id={AGENT_COMMAND_TABLE_IDS.packages}
            mode="server"
            columns={packageColumns}
            initialState={initialState}
            search={{ placeholder: 'Search packages, sources, projects...' }}
            features={{ enableSorting: true, enableFiltering: true }}
            queryFn={packagesQueryFn}
            getQueryKeysFromParams={packagesQueryKeys}
            onRowClick={setDetailPackage}
            getRowClassName={getRowClassName}
            noDataLabel="No package installs recorded yet."
            errorBoundaryLabel="Something went wrong loading package activity"
          />
        </TabsContent>

        <TabsContent value="repositories">
          <DataTable<AggregatedRepository>
            id={AGENT_COMMAND_TABLE_IDS.repositories}
            mode="server"
            columns={gitColumns}
            initialState={initialState}
            search={{ placeholder: 'Search repositories, projects...' }}
            features={{ enableSorting: true, enableFiltering: true }}
            queryFn={reposQueryFn}
            getQueryKeysFromParams={reposQueryKeys}
            onRowClick={setDetailRepo}
            getRowClassName={getRowClassName}
            noDataLabel="No repository access recorded yet."
            errorBoundaryLabel="Something went wrong loading repository activity"
          />
        </TabsContent>
      </Tabs>

      {/* Aggregated Packages dialogs (KIR-3807). */}
      <AggregatedPackageDetailDialog
        organizationId={organizationId}
        pkg={detailPackage}
        onClose={() => setDetailPackage(null)}
      />
      <PackageOccurrencesDialog
        organizationId={organizationId}
        pkg={occurrencesPackage}
        onClose={() => setOccurrencesPackage(null)}
      />
      {/* Aggregated Repositories dialogs (KIR-3807). */}
      <AggregatedRepositoryDetailDialog
        organizationId={organizationId}
        repo={detailRepo}
        onClose={() => setDetailRepo(null)}
      />
      <RepositoryOccurrencesDialog
        organizationId={organizationId}
        repo={occurrencesRepo}
        onClose={() => setOccurrencesRepo(null)}
      />
    </div>
  );
}

AgentsActivityTab.displayName = 'AgentsActivityTab';
